import { loadMapboxGl, mapboxToken, mapFallbackMarkup } from "./src/mapbox-client.js";
import { isValidCoordinatePair } from "./photo-location-state.js";

let activeMap = null;
let mapLifecycleGeneration = 0;

const DEFAULT_MAP_COPY = Object.freeze({
  map_loading: "Loading map...",
  map_load_failed: "Mapbox failed to load",
  map_unavailable_detail: "The map is unavailable right now.",
  map_token_needed: "Mapbox token needed",
  map_location_token_help: "Open this page with ?mapboxToken=YOUR_TOKEN to render the location map.",
});

function defaultMapCopy(key) {
  return DEFAULT_MAP_COPY[key] || "";
}

export function cleanupPhotoLocationMap() {
  mapLifecycleGeneration += 1;
  if (!activeMap) return;
  const { map, marker, onDragEnd } = activeMap;
  activeMap = null;
  try {
    if (marker && onDragEnd && typeof marker.off === "function") {
      marker.off("dragend", onDragEnd);
    }
    if (marker && typeof marker.remove === "function") marker.remove();
    if (map && typeof map.remove === "function") map.remove();
  } catch {
    // Best-effort cleanup; the form must stay usable even if Mapbox is mid-teardown.
  }
}

export function resizePhotoLocationMap() {
  const map = activeMap?.map;
  if (!map || !map.getContainer?.().isConnected) return;
  window.requestAnimationFrame(() => map.resize());
}

export async function initializePhotoLocationMap({
  container,
  coordinates,
  onDragEnd,
  onError,
  token = mapboxToken(),
  loadMapbox = loadMapboxGl,
  mapboxgl = window.mapboxgl,
  copy = defaultMapCopy,
} = {}) {
  cleanupPhotoLocationMap();
  const generation = mapLifecycleGeneration;
  let map = null;
  let marker = null;

  if (!container || !container.isConnected || !isValidCoordinatePair(coordinates)) return null;
  if (!token) {
    const message = copy("map_location_token_help");
    container.innerHTML = mapFallbackMarkup(copy("map_token_needed"), message);
    onError?.(message);
    return null;
  }

  container.innerHTML = `<div class="map-fallback"><strong>${copy("map_loading")}</strong></div>`;

  try {
    await loadMapbox();
    if (generation !== mapLifecycleGeneration) return null;
    if (!container.isConnected) return null;
    const gl = mapboxgl || window.mapboxgl;
    if (!gl?.Map || !gl?.Marker) throw new Error("Mapbox GL is unavailable.");
    gl.accessToken = token;
    container.innerHTML = "";

    const center = [coordinates.longitude, coordinates.latitude];
    map = new gl.Map({
      container,
      style: "mapbox://styles/mapbox/light-v11",
      center,
      zoom: 15,
      attributionControl: false,
    });
    marker = new gl.Marker({ draggable: true })
      .setLngLat(center)
      .addTo(map);
    const handleDragEnd = () => {
      const lngLat = marker.getLngLat();
      onDragEnd?.({ latitude: lngLat.lat, longitude: lngLat.lng });
    };

    marker.on("dragend", handleDragEnd);
    if (gl.AttributionControl) {
      map.addControl(new gl.AttributionControl({ compact: true }), "bottom-right");
    }
    map.on("load", () => resizePhotoLocationMap());
    if (generation !== mapLifecycleGeneration || !container.isConnected) {
      if (typeof marker.remove === "function") marker.remove();
      if (typeof map.remove === "function") map.remove();
      return null;
    }
    activeMap = { container, map, marker, onDragEnd: handleDragEnd };
    resizePhotoLocationMap();
    return activeMap;
  } catch (error) {
    if (marker && typeof marker.remove === "function") marker.remove();
    if (map && typeof map.remove === "function") map.remove();
    if (container.isConnected) {
      container.innerHTML = mapFallbackMarkup(copy("map_load_failed"), copy("map_unavailable_detail"));
    }
    onError?.(error.message);
    return null;
  }
}
